<template>
  <div class="content d-flex flex-column flex-column-fluid" id="kt_content">
    <div class="toolbar" id="kt_toolbar">
      <div id="kt_toolbar_container" class="container-fluid d-flex flex-stack">
        <div class="page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0">
          <h1 class="d-flex align-items-center text-dark fw-bolder fs-3 my-1">
            {{ $t('landlord.shops.detail_title') }}
          </h1>
          <span class="h-20px border-gray-300 border-start mx-4"></span>
          <span class="text-muted fs-6">{{ shop?.name || shopId }}</span>
        </div>
        <div class="d-flex align-items-center gap-2">
          <router-link to="/landlord/shops" class="btn btn-sm btn-light">
            <i class="fas fa-arrow-left me-1"></i> {{ $t('landlord.shops.back') }}
          </router-link>
        </div>
      </div>
    </div>
    <div class="post d-flex flex-column-fluid" id="kt_post">
      <div id="kt_content_container" class="container-fluid">

        <div v-if="loading" class="text-center py-20">
          <span class="spinner-border text-primary"></span>
        </div>

        <template v-else-if="shop">
          <!-- Header card -->
          <div class="card card-flush mb-5">
            <div class="card-body d-flex align-items-center justify-content-between py-5">
              <div class="d-flex align-items-center gap-4">
                <img v-if="shop.logo" :src="shop.logo" class="rounded" style="width:56px;height:56px;object-fit:cover;" />
                <div v-else class="rounded bg-light-primary d-flex align-items-center justify-content-center" style="width:56px;height:56px;">
                  <i class="fas fa-store fs-2 text-primary"></i>
                </div>
                <div>
                  <div class="d-flex align-items-center gap-3">
                    <h2 class="fw-bolder mb-0">{{ shop.name || shopId }}</h2>
                    <span class="badge" :class="shop.online ? 'badge-light-success' : 'badge-light-danger'">
                      {{ shop.online ? $t('landlord.shops.active') : $t('landlord.shops.inactive') }}
                    </span>
                    <span v-if="shop.signup?.pending" class="badge badge-warning cursor-pointer" @click="activeTab = 'validation'">
                      {{ $t('landlord.shops.pending_' + shop.signup.kind) }}
                    </span>
                    <span v-if="shop.signup?.is_trial" class="badge badge-light-info">
                      <i class="fas fa-flask me-1 fs-9 text-info"></i>{{ $t('landlord.signup.trial_badge') }}
                      <template v-if="!shop.signup.pending && shop.signup.expires_at"> · {{ $t('landlord.signup.until', { date: formatDate(shop.signup.expires_at) }) }}</template>
                    </span>
                  </div>
                  <div class="text-gray-400 fs-7 mt-1">ID: {{ shop.id }}</div>
                  <div class="mt-1" v-if="shop.domain">
                    <span class="badge badge-light-primary fs-8">
                      <i class="fas fa-globe me-1 fs-9"></i>{{ shop.domain }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="d-flex gap-2">
                <button @click="onToggleStatus" class="btn btn-sm"
                        :class="shop.online ? 'btn-light-warning' : 'btn-light-success'">
                  <i :class="shop.online ? 'fas fa-ban me-1' : 'fas fa-check me-1'"></i>
                  {{ shop.online ? $t('landlord.shops.deactivate') : $t('landlord.shops.activate') }}
                </button>
                <button v-if="activeTab === 'info' && !editMode" @click="editMode = true" class="btn btn-sm btn-light-primary">
                  <i class="fas fa-edit me-1"></i> {{ $t('landlord.shops.edit') }}
                </button>
              </div>
            </div>
          </div>

          <!-- Options row -->
          <div class="card card-flush mb-5">
            <div class="card-body py-4 px-6">
              <div class="d-flex flex-wrap align-items-center gap-6">
                <!-- Abonnement courant -->
                <div class="d-flex align-items-center gap-3">
                  <div class="symbol symbol-40px">
                    <div class="symbol-label bg-light-info">
                      <i class="fas fa-calendar-check fs-4 text-info"></i>
                    </div>
                  </div>
                  <div>
                    <div class="text-muted fs-8">{{ $t('landlord.shops.subscription_label') }}</div>
                    <div v-if="shop.plan" class="fw-bolder fs-6">{{ shop.plan?.name || '—' }}</div>
                    <!-- inscription en attente : l'offre demandée sera activée par la validation -->
                    <div v-else-if="shop.signup?.pending && shop.signup.plan" class="fw-bolder fs-6">
                      {{ shop.signup.plan.name }} <span class="text-warning fs-8">({{ $t('landlord.signup.requested') }})</span>
                    </div>
                    <div v-else class="fw-bolder fs-7 text-danger">{{ $t('landlord.license.no_plan') }}</div>
                  </div>
                </div>

                <span class="border-start h-40px d-none d-md-block"></span>

                <!-- Limites -->
                <div class="d-flex align-items-center gap-3">
                  <div class="symbol symbol-40px">
                    <div class="symbol-label bg-light-warning">
                      <i class="fas fa-sliders-h fs-4 text-warning"></i>
                    </div>
                  </div>
                  <div class="text-muted fs-8 fw-bold">{{ $t('landlord.shops.limits_label') }}</div>
                </div>

                <!-- La limite d'utilisateurs se règle sur la société (abonnement, toutes boutiques confondues) -->
                <div class="d-flex align-items-center gap-2">
                  <label class="text-muted fs-8 mb-0">
                    <i class="fas fa-tags fs-9 me-1"></i>{{ $t('landlord.shops.max_categories_label') }}
                  </label>
                  <input v-model.number="limitsInput.max_categories" type="number" min="0"
                         class="form-control form-control-sm text-center fw-bolder" style="width:70px" />
                </div>
                <div class="d-flex align-items-center gap-2">
                  <label class="text-muted fs-8 mb-0">
                    <i class="fas fa-box fs-9 me-1"></i>{{ $t('landlord.shops.max_products_label') }}
                  </label>
                  <input v-model.number="limitsInput.max_products" type="number" min="0"
                         class="form-control form-control-sm text-center fw-bolder" style="width:70px" />
                </div>

                <button @click="onSetLimits" class="btn btn-sm btn-primary ms-auto" :disabled="savingLimits">
                  <span v-if="savingLimits" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="fas fa-check fs-9 me-1"></i>{{ $t('landlord.shops.save') }}
                </button>
              </div>
            </div>
          </div>

          <!-- Tabs -->
          <div class="card card-flush">
            <div class="card-header border-0 pt-6 pb-0">
              <ul class="nav nav-tabs nav-line-tabs nav-line-tabs-2x fs-6 fw-bolder">
                <li v-if="shop.signup?.pending || shop.signup?.validated_at" class="nav-item">
                  <a class="nav-link" :class="{ active: activeTab === 'validation' }" href="#" @click.prevent="activeTab = 'validation'">
                    <i class="fas fa-clipboard-check me-2"></i> {{ $t('landlord.signup.tab') }}
                    <span v-if="shop.signup.pending" class="badge badge-warning ms-1">1</span>
                  </a>
                </li>
                <li class="nav-item">
                  <a class="nav-link" :class="{ active: activeTab === 'stats' }" href="#" @click.prevent="activeTab = 'stats'">
                    <i class="fas fa-chart-bar me-2"></i> {{ $t('landlord.shops.stats_title') }}
                  </a>
                </li>
                <li class="nav-item">
                  <a class="nav-link" :class="{ active: activeTab === 'info' }" href="#" @click.prevent="switchToInfo">
                    <i class="fas fa-info-circle me-2"></i> {{ $t('landlord.shops.information') }}
                  </a>
                </li>
                <li class="nav-item">
                  <a class="nav-link" :class="{ active: activeTab === 'domain' }" href="#" @click.prevent="switchToDomain">
                    <i class="fas fa-globe me-2"></i> {{ $t('landlord.shops.domain') }}
                  </a>
                </li>
                <li class="nav-item">
                  <a class="nav-link" :class="{ active: activeTab === 'users' }" href="#" @click.prevent="switchToUsers">
                    <i class="fas fa-users me-2"></i> {{ $t('landlord.shops.users_tab') }}
                    <span v-if="users.length" class="badge badge-light-primary ms-1">{{ users.length }}</span>
                  </a>
                </li>
                <li class="nav-item">
                  <a class="nav-link" :class="{ active: activeTab === 'license' }" href="#" @click.prevent="switchToLicense">
                    <i class="fas fa-key me-2"></i> {{ $t('landlord.license.title') }}
                  </a>
                </li>
                <li class="nav-item">
                  <a class="nav-link" :class="{ active: activeTab === 'plan_history' }" href="#" @click.prevent="switchToPlanHistory">
                    <i class="fas fa-history me-2"></i> {{ $t('landlord.license.plan_history') }}
                  </a>
                </li>
                <li class="nav-item">
                  <a class="nav-link" :class="{ active: activeTab === 'branding' }" href="#" @click.prevent="switchToBranding">
                    <i class="fas fa-palette me-2"></i> {{ $t('landlord.branding.title') }}
                  </a>
                </li>
              </ul>
            </div>
            <div class="card-body py-6">
              <!-- TAB: Validation de l'inscription (landing : essai ou offre payée) -->
              <div v-if="activeTab === 'validation' && shop.signup">
                <div class="row g-6">
                  <div class="col-lg-7">
                    <h5 class="fw-bolder text-gray-800 mb-4">{{ $t('landlord.signup.request_title') }}</h5>
                    <table class="table table-row-dashed align-middle fs-6 gy-3 mb-0">
                      <tbody>
                        <tr>
                          <td class="text-muted w-200px">{{ $t('landlord.signup.type') }}</td>
                          <td class="fw-bold">
                            <span v-if="shop.signup.is_trial" class="badge badge-light-info"><i class="fas fa-flask me-1 text-info"></i>{{ $t('landlord.signup.type_trial') }}</span>
                            <span v-else class="badge badge-light-primary">{{ $t('landlord.signup.type_paid') }}</span>
                          </td>
                        </tr>
                        <tr>
                          <td class="text-muted">{{ $t('landlord.signup.offer') }}</td>
                          <td>
                            <div class="fw-bolder">{{ shop.signup.plan?.name || '—' }}
                              <span v-if="!shop.signup.is_trial && shop.signup.billing_period" class="text-muted fw-normal fs-7">· {{ $t('landlord.plans.period_' + shop.signup.billing_period) }}</span>
                            </div>
                            <div v-if="shop.signup.plan?.modules?.length" class="d-flex flex-wrap gap-1 mt-2">
                              <span v-for="mod in shop.signup.plan.modules" :key="mod" class="badge badge-light fs-8">{{ mod }}</span>
                            </div>
                          </td>
                        </tr>
                        <tr v-if="shop.signup.requested_modules?.length">
                          <td class="text-muted">{{ $t('landlord.signup.extra_modules') }}</td>
                          <td>
                            <span v-for="mod in shop.signup.requested_modules" :key="mod.id" class="badge badge-light-success fs-8 me-1">{{ mod.name }}</span>
                          </td>
                        </tr>
                        <tr v-if="!shop.signup.is_trial">
                          <td class="text-muted">{{ $t('landlord.signup.payment') }}</td>
                          <td>
                            <span v-if="shop.signup.payment_reference" class="text-success fw-bold">
                              <i class="fas fa-check-circle text-success me-1"></i>{{ formatPrice(shop.signup.amount) }} · {{ shop.signup.payment_reference }}
                            </span>
                            <span v-else-if="shop.signup.kind === 'unpaid'" class="text-danger fw-bold">{{ $t('landlord.shops.pending_unpaid') }}</span>
                            <span v-else>{{ formatPrice(shop.signup.amount) }}</span>
                          </td>
                        </tr>
                        <tr v-if="shop.signup.owner">
                          <td class="text-muted">{{ $t('landlord.signup.contact') }}</td>
                          <td>
                            <div class="fw-bold">{{ shop.signup.owner.name }}</div>
                            <div class="text-gray-600 fs-7">{{ shop.signup.owner.email }} · {{ shop.signup.owner.phone }}</div>
                          </td>
                        </tr>
                        <tr v-if="shop.signup.requested_at">
                          <td class="text-muted">{{ $t('landlord.signup.requested_at') }}</td>
                          <td>{{ formatDate(shop.signup.requested_at) }}</td>
                        </tr>
                        <tr v-if="shop.signup.validated_at">
                          <td class="text-muted">{{ $t('landlord.signup.validated_at') }}</td>
                          <td>
                            {{ formatDate(shop.signup.validated_at) }}
                            <span v-if="shop.signup.expires_at" class="text-muted fs-7"> · {{ $t('landlord.signup.until', { date: formatDate(shop.signup.expires_at) }) }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                  </div>

                  <div class="col-lg-5">
                    <div v-if="shop.signup.pending" class="bg-light-warning rounded p-6">
                      <h5 class="fw-bolder mb-3">{{ $t('landlord.signup.before_title') }}</h5>
                      <ul class="text-gray-700 fs-7 ps-5 mb-5">
                        <li class="mb-1">
                          <a href="#" @click.prevent="switchToLicense">{{ $t('landlord.signup.check_license') }}</a>
                        </li>
                        <li class="mb-1">
                          <a href="#" @click.prevent="switchToDomain">{{ $t('landlord.signup.check_domain') }}</a>
                          <span v-if="shop.domain" class="text-muted"> ({{ shop.domain }})</span>
                        </li>
                        <li class="mb-1">{{ $t('landlord.signup.check_recaptcha') }}</li>
                        <li>{{ $t('landlord.signup.check_branding') }}</li>
                      </ul>
                      <p class="text-gray-700 fs-7 mb-5">
                        {{ shop.signup.is_trial ? $t('landlord.signup.validate_trial_help') : $t('landlord.signup.validate_paid_help') }}
                      </p>
                      <button class="btn btn-success w-100" :disabled="validating || shop.signup.kind === 'unpaid'" @click="onValidateSignup">
                        <span v-if="validating" class="spinner-border spinner-border-sm me-1"></span>
                        <i v-else class="fas fa-check-double me-1"></i>{{ $t('landlord.signup.validate') }}
                      </button>
                      <div v-if="shop.signup.kind === 'unpaid'" class="text-danger fs-8 mt-2">{{ $t('landlord.signup.unpaid_help') }}</div>
                    </div>
                    <div v-else class="bg-light-success rounded p-6">
                      <i class="fas fa-check-circle text-success fs-2 me-2"></i>
                      <span class="fw-bold">{{ $t('landlord.signup.done') }}</span>
                    </div>
                  </div>
                </div>
              </div>


              <!-- TAB: Stats -->
              <div v-if="activeTab === 'stats'">
                <div class="d-flex justify-content-end mb-5">
                  <button @click="loadStats" class="btn btn-sm btn-light-primary" :disabled="loadingStats">
                    <span v-if="loadingStats" class="spinner-border spinner-border-sm me-1"></span>
                    <i v-else class="fas fa-sync-alt me-1"></i>
                    {{ $t('landlord.shops.stats_refresh') }}
                  </button>
                </div>
                <div v-if="loadingStats" class="text-center py-10">
                  <span class="spinner-border text-primary"></span>
                </div>
                <div v-else-if="statsError" class="text-center py-10 text-danger">
                  <i class="fas fa-exclamation-triangle fs-2 mb-3 d-block"></i>
                  {{ statsError }}
                </div>
                <div v-else-if="stats">
                <div class="row row-cols-2 row-cols-md-3 row-cols-xl-5 g-4">
                  <div class="col">
                    <div class="bg-light-info rounded p-4 d-flex align-items-center">
                      <i class="fas fa-users fs-2x text-info me-3"></i>
                      <div>
                        <div class="fs-3 fw-bolder text-dark">{{ stats.users || 0 }}</div>
                        <div class="text-gray-500 fs-8 fw-bold">{{ $t('landlord.shops.stats_users') }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="col">
                    <div class="bg-light-warning rounded p-4 d-flex align-items-center">
                      <i class="fas fa-address-book fs-2x text-warning me-3"></i>
                      <div>
                        <div class="fs-3 fw-bolder text-dark">{{ stats.contacts || 0 }}</div>
                        <div class="text-gray-500 fs-8 fw-bold">{{ $t('landlord.shops.stats_contacts') }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="col">
                    <div class="bg-light-primary rounded p-4 d-flex align-items-center">
                      <i class="fas fa-box fs-2x text-primary me-3"></i>
                      <div>
                        <div class="fs-3 fw-bolder text-dark">{{ stats.products || 0 }}</div>
                        <div class="text-gray-500 fs-8 fw-bold">{{ $t('landlord.shops.stats_products') }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="col">
                    <div class="bg-light-success rounded p-4 d-flex align-items-center">
                      <i class="fas fa-shopping-cart fs-2x text-success me-3"></i>
                      <div>
                        <div class="fs-3 fw-bolder text-dark">{{ stats.orders || 0 }}</div>
                        <div class="text-gray-500 fs-8 fw-bold">{{ $t('landlord.shops.stats_orders') }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="col">
                    <div class="bg-light-dark rounded p-4 d-flex align-items-center">
                      <i class="fas fa-eye fs-2x text-dark me-3"></i>
                      <div>
                        <div class="fs-3 fw-bolder text-dark">{{ stats.visits || 0 }}</div>
                        <div class="text-gray-500 fs-8 fw-bold">{{ $t('landlord.shops.stats_visits') }}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Hors de la grille des chiffres : ses colonnes (1/5 de large) écrasaient ces cartes -->
                <div v-if="topCountries.length || pwaInstalls" class="row g-4 mt-2">
                      <!-- Pays des visiteurs : part des visites des 30 derniers jours -->
                      <div v-if="topCountries.length" class="col-md-6">
                        <div class="border border-gray-200 rounded p-5 h-100">
                          <div class="d-flex align-items-center mb-4">
                            <i class="fas fa-globe-africa text-primary fs-3 me-2"></i>
                            <span class="fw-bolder text-dark fs-6">{{ $t('landlord.shops.stats_top_countries') }}</span>
                          </div>
                          <div v-for="c in topCountries" :key="c.country" class="mb-4">
                            <div class="d-flex align-items-center justify-content-between mb-1">
                              <span class="fw-bold text-gray-800 fs-7">
                                <span class="me-2 fs-5">{{ flag(c.country_code) }}</span>{{ c.country }}
                              </span>
                              <span class="fs-7 text-gray-600">
                                <span class="fw-bolder text-dark">{{ c.count }}</span>
                                <span class="ms-1">· {{ percent(c.count, stats.visits) }}%</span>
                              </span>
                            </div>
                            <div class="progress h-6px bg-light-primary">
                              <div class="progress-bar bg-primary" :style="{ width: percent(c.count, topCountries[0].count) + '%' }"></div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Installations de l'application -->
                      <div v-if="pwaInstalls" class="col-md-6">
                        <div class="border border-gray-200 rounded p-5 h-100">
                          <div class="d-flex align-items-center mb-4">
                            <i class="fas fa-mobile-alt text-primary fs-3 me-2"></i>
                            <span class="fw-bolder text-dark fs-6">{{ $t('landlord.shops.stats_pwa_title') }}</span>
                          </div>
                          <div class="d-flex align-items-end gap-6 mb-5">
                            <div>
                              <div class="fs-2hx fw-bolder text-dark lh-1">{{ pwaInstalls.total }}</div>
                              <div class="text-gray-500 fs-8 fw-bold mt-1">{{ $t('landlord.shops.stats_pwa_devices') }}</div>
                            </div>
                            <div>
                              <div class="fs-2 fw-bolder text-success lh-1">{{ pwaInstalls.active_30d }}</div>
                              <div class="text-gray-500 fs-8 fw-bold mt-1">{{ $t('landlord.shops.stats_pwa_active_short') }}</div>
                            </div>
                          </div>
                          <div v-for="p in pwaPlatforms" :key="p.key" class="mb-4">
                            <div class="d-flex align-items-center justify-content-between mb-1">
                              <span class="fw-bold text-gray-800 fs-7">
                                <i :class="p.icon" class="fs-5 me-2" style="width: 18px"></i>{{ p.label }}
                              </span>
                              <span class="fs-7 text-gray-600">
                                <span class="fw-bolder text-dark">{{ p.count }}</span>
                                <span class="ms-1">· {{ percent(p.count, pwaInstalls.total) }}%</span>
                              </span>
                            </div>
                            <div class="progress h-6px" :class="p.track">
                              <div class="progress-bar" :class="p.bar" :style="{ width: percent(p.count, pwaInstalls.total) + '%' }"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                </div>

                <!-- Dernières visites : heure, lieu, site d'origine et appareil -->
                <div v-if="lastVisits.length" class="border border-gray-200 rounded p-5 mt-6">
                  <div class="d-flex align-items-center mb-4">
                    <i class="fas fa-history text-primary fs-3 me-2"></i>
                    <span class="fw-bolder text-dark fs-6">{{ $t('landlord.shops.stats_last_visits') }}</span>
                  </div>
                  <div class="table-responsive">
                    <table class="table table-row-dashed align-middle fs-7 gy-3 mb-0">
                      <thead>
                        <tr class="text-gray-500 fw-bolder fs-8 text-uppercase">
                          <th>{{ $t('landlord.shops.stats_visit_when') }}</th>
                          <th>{{ $t('landlord.shops.stats_visit_where') }}</th>
                          <th>{{ $t('landlord.shops.stats_visit_source') }}</th>
                          <th>{{ $t('landlord.shops.stats_visit_device') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(v, i) in lastVisits" :key="i">
                          <td class="text-nowrap fw-bold text-gray-800">{{ visitDate(v.created_at) }}</td>
                          <td>
                            <span class="me-2 fs-5">{{ flag(v.country_code) }}</span>
                            <span v-if="v.city">{{ v.city }}, </span>{{ v.country || $t('landlord.shops.stats_visit_unknown') }}
                          </td>
                          <td>
                            <span class="badge" :class="v.source && v.source !== 'direct' ? 'badge-light-primary' : 'badge-light'">{{ visitSource(v.source) }}</span>
                          </td>
                          <td class="text-nowrap">
                            <i :class="visitDevice(v.device).icon" class="me-1"></i>{{ visitDevice(v.device).label }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                </div>
                <div v-else class="text-center py-10 text-gray-500">
                  {{ $t('landlord.shops.stats_empty') }}
                </div>
              </div>

              <!-- TAB: Informations -->
              <div v-if="activeTab === 'info'">
                <!-- View mode -->
                <template v-if="!editMode">
                  <div class="row g-5">
                    <div class="col-md-6">
                      <div class="fs-7 text-gray-500 fw-bold mb-1">{{ $t('landlord.shops.fields.name') }}</div>
                      <div class="fs-6 text-dark">{{ shop.name || '-' }}</div>
                    </div>
                    <div class="col-md-6">
                      <div class="fs-7 text-gray-500 fw-bold mb-1">{{ $t('landlord.shops.fields.domain') }}</div>
                      <div class="fs-6 text-dark">{{ shop.domain || '-' }}</div>
                    </div>
                    <div class="col-md-6">
                      <div class="fs-7 text-gray-500 fw-bold mb-1">{{ $t('landlord.shops.fields.society_email') }}</div>
                      <div class="fs-6 text-dark">{{ shop.society_email || '-' }}</div>
                    </div>
                    <div class="col-md-6">
                      <div class="fs-7 text-gray-500 fw-bold mb-1">{{ $t('landlord.shops.fields.society_phone') }}</div>
                      <div class="fs-6 text-dark">{{ shop.society_phone ? [shop.society_phone_code, shop.society_phone].filter(Boolean).join(' ') : '-' }}</div>
                    </div>
                    <div class="col-md-6">
                      <div class="fs-7 text-gray-500 fw-bold mb-1">{{ $t('landlord.shops.fields.address') }}</div>
                      <div class="fs-6 text-dark">{{ shop.address || '-' }}</div>
                    </div>
                    <div class="col-md-6">
                      <div class="fs-7 text-gray-500 fw-bold mb-1">{{ $t('landlord.shops.fields.phone') }}</div>
                      <div class="fs-6 text-dark">{{ shop.phone ? [shop.phone_code, shop.phone].filter(Boolean).join(' ') : '-' }}</div>
                    </div>
                    <div class="col-md-6">
                      <div class="fs-7 text-gray-500 fw-bold mb-1">{{ $t('landlord.shops.fields.country') }}</div>
                      <div class="fs-6 text-dark">{{ shop.country || '-' }}</div>
                    </div>
                    <div class="col-md-6">
                      <div class="fs-7 text-gray-500 fw-bold mb-1">{{ $t('landlord.shops.fields.sector') }}</div>
                      <div class="fs-6 text-dark">{{ shop.sector || '-' }}</div>
                    </div>
                  </div>

                  <!-- Visuels -->
                  <div class="mt-8">
                    <h5 class="fw-bolder text-gray-800 mb-4">{{ $t('landlord.shops.section_visuals') }}</h5>
                    <div class="separator separator-dashed mb-5"></div>
                    <div class="row g-5">
                      <div class="col-md-4">
                        <div class="fs-7 text-gray-500 fw-bold mb-2">{{ $t('landlord.shops.fields.logo') }}</div>
                        <template v-if="shop.logo">
                          <div class="border rounded p-3 bg-light text-center">
                            <img :src="shop.logo" class="img-fluid rounded" style="max-height: 120px; object-fit: contain;" />
                          </div>
                        </template>
                        <div v-else class="text-gray-400 fs-7">{{ $t('landlord.shops.no_file') }}</div>
                      </div>
                      <div class="col-md-4">
                        <div class="fs-7 text-gray-500 fw-bold mb-2">{{ $t('landlord.shops.fields.favicon') }}</div>
                        <template v-if="shop.favicon">
                          <div class="border rounded p-3 bg-light text-center">
                            <img :src="shop.favicon" class="img-fluid rounded" style="max-height: 64px; object-fit: contain;" />
                          </div>
                        </template>
                        <div v-else class="text-gray-400 fs-7">{{ $t('landlord.shops.no_file') }}</div>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Edit mode -->
                <template v-else>
                  <form @submit.prevent="onSave">
                    <div class="row g-5 mb-8">
                      <div class="col-md-6">
                        <label class="form-label fs-7 fw-bold">{{ $t('landlord.shops.fields.name') }}</label>
                        <input v-model="editForm.name" type="text" class="form-control form-control-solid" />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label fs-7 fw-bold">{{ $t('landlord.shops.fields.society_email') }}</label>
                        <input v-model="editForm.society_email" type="email" class="form-control form-control-solid" />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label fs-7 fw-bold">{{ $t('landlord.shops.fields.society_phone') }}</label>
                        <InputTel
                            name="shop_society_phone"
                            containerClass=""
                            v-model:phone="editForm.society_phone"
                            v-model:phone-code="editForm.society_phone_code"
                        />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label fs-7 fw-bold">{{ $t('landlord.shops.fields.address') }}</label>
                        <input v-model="editForm.address" type="text" class="form-control form-control-solid" />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label fs-7 fw-bold">{{ $t('landlord.shops.fields.phone') }}</label>
                        <InputTel
                            name="shop_phone"
                            containerClass=""
                            v-model:phone="editForm.phone"
                            v-model:phone-code="editForm.phone_code"
                        />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label fs-7 fw-bold">{{ $t('landlord.shops.fields.country') }}</label>
                        <select v-model="editForm.country_id" class="form-select form-select-solid">
                          <option value="">--</option>
                          <option v-for="country in countries" :key="country.id" :value="country.id">{{ country.name }}</option>
                        </select>
                      </div>
                      <div class="col-md-6">
                        <label class="form-label fs-7 fw-bold">{{ $t('landlord.shops.fields.sector') }}</label>
                        <select v-model="editForm.sector_id" class="form-select form-select-solid">
                          <option value="">{{ $t('landlord.shops.fields.sector_placeholder') }}</option>
                          <option v-for="sector in sectors" :key="sector.id" :value="sector.id">{{ sector.name }}</option>
                        </select>
                      </div>
                    </div>

                    <!-- Visuels -->
                    <div class="mb-8">
                      <h5 class="fw-bolder text-gray-800 mb-4">{{ $t('landlord.shops.section_visuals') }}</h5>
                      <div class="separator separator-dashed mb-5"></div>
                      <div class="row g-5">
                        <div class="col-md-6">
                          <label class="form-label fs-7 fw-bold d-block mb-3">{{ $t('landlord.shops.fields.logo') }}</label>
                          <div class="image-input image-input-outline" data-kt-image-input="true">
                            <div class="image-input-wrapper w-125px h-125px" :style="logoEditBackground"></div>
                            <label class="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow"
                                   data-kt-image-input-action="change" title="Changer le logo">
                              <i class="bi bi-pencil-fill fs-7"></i>
                              <input type="file" @change="onEditFileChange($event, 'logo')" accept=".png,.jpg,.jpeg,.svg" />
                              <input type="hidden" name="logo_remove" />
                            </label>
                            <span class="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow"
                                  @click="removeEditImageFile('logo')" title="Supprimer">
                              <i class="bi bi-x fs-2"></i>
                            </span>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <label class="form-label fs-7 fw-bold d-block mb-3">{{ $t('landlord.shops.fields.favicon') }}</label>
                          <div class="image-input image-input-outline" data-kt-image-input="true">
                            <div class="image-input-wrapper w-65px h-65px" :style="faviconEditBackground"></div>
                            <label class="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow"
                                   data-kt-image-input-action="change" title="Changer le favicon">
                              <i class="bi bi-pencil-fill fs-7"></i>
                              <input type="file" @change="onEditFileChange($event, 'favicon')" accept=".png,.jpg,.jpeg,.ico,.svg" />
                              <input type="hidden" name="favicon_remove" />
                            </label>
                            <span class="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow"
                                  @click="removeEditImageFile('favicon')" title="Supprimer">
                              <i class="bi bi-x fs-2"></i>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="d-flex justify-content-end gap-3 mt-8">
                      <button type="button" @click="cancelEdit" class="btn btn-light">
                        {{ $t('landlord.shops.cancel') }}
                      </button>
                      <button type="submit" class="btn btn-primary" :disabled="saving">
                        <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                        {{ $t('landlord.shops.save') }}
                      </button>
                    </div>
                  </form>
                </template>
              </div>

              <!-- TAB: Domaine -->
              <div v-if="activeTab === 'domain'">
                <div class="row g-5">
                  <div class="col-md-8">
                    <label class="form-label fw-bold">{{ $t('landlord.shops.fields.domain') }}</label>
                    <div class="d-flex gap-2">
                      <input v-model="domainInput" type="text" class="form-control form-control-solid"
                             :placeholder="$t('landlord.shops.fields.domain_placeholder')"
                             @keyup.enter="onSaveDomain" />
                      <button @click="onSaveDomain" class="btn btn-primary" :disabled="savingDomain">
                        <span v-if="savingDomain" class="spinner-border spinner-border-sm me-1"></span>
                        {{ $t('landlord.shops.save') }}
                      </button>
                    </div>
                    <span class="text-muted fs-8 mt-2 d-block">{{ $t('landlord.shops.domain_hint') }}</span>
                  </div>
                </div>
              </div>

              <!-- TAB: Utilisateurs -->
              <div v-if="activeTab === 'users'">
                <div class="d-flex justify-content-between align-items-center mb-5">
                  <h5 class="fw-bolder text-gray-800 mb-0">{{ $t('landlord.shops.users_title') }}</h5>
                  <button @click="openUserModal(null)" class="btn btn-sm btn-primary">
                    <i class="fas fa-plus me-1"></i> {{ $t('landlord.shops.user_add') }}
                  </button>
                </div>

                <div v-if="loadingUsers" class="text-center py-10">
                  <span class="spinner-border text-primary"></span>
                </div>
                <div v-else-if="!users.length" class="text-center py-10 text-gray-500">
                  {{ $t('landlord.shops.users_empty') }}
                </div>
                <div v-else class="table-responsive">
                  <table class="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                    <thead>
                      <tr class="fw-bolder text-muted">
                        <th>{{ $t('landlord.shops.user_name') }}</th>
                        <th>{{ $t('landlord.shops.fields.admin_email') }}</th>
                        <th>{{ $t('landlord.shops.fields.admin_phone') }}</th>
                        <th>{{ $t('landlord.shops.user_role') }}</th>
                        <th>{{ $t('landlord.shops.col_status') }}</th>
                        <th class="text-end">{{ $t('landlord.shops.col_actions') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="user in users" :key="user.id">
                        <td class="fw-bold">{{ user.lastname }} {{ user.firstname }}</td>
                        <td>{{ user.email }}</td>
                        <td>{{ user.phone ? [user.phone_code, user.phone].filter(Boolean).join(' ') : '-' }}</td>
                        <td><span class="badge badge-light-primary">{{ user.role || '-' }}</span></td>
                        <td>
                          <span class="badge" :class="user.is_active ? 'badge-light-success' : 'badge-light-danger'">
                            {{ user.is_active ? $t('landlord.shops.active') : $t('landlord.shops.inactive') }}
                          </span>
                        </td>
                        <td class="text-end">
                          <button @click="openUserModal(user)" class="btn btn-icon btn-sm btn-light-primary me-1" :title="$t('landlord.shops.edit')">
                            <i class="fas fa-edit"></i>
                          </button>
                          <button @click="onToggleUserActive(user)" class="btn btn-icon btn-sm"
                                  :class="user.is_active ? 'btn-light-warning' : 'btn-light-success'"
                                  :title="user.is_active ? $t('landlord.shops.deactivate') : $t('landlord.shops.activate')">
                            <i :class="user.is_active ? 'fas fa-ban' : 'fas fa-check'"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- TAB: Licence -->
              <div v-if="activeTab === 'license'">
                <div v-if="loadingLicense" class="text-center py-10">
                  <span class="spinner-border text-primary"></span>
                </div>
                <template v-else>
                  <!-- Current Plan -->
                  <div class="mb-8">
                    <h5 class="fw-bolder text-gray-800 mb-4">{{ $t('landlord.license.current_plan') }}</h5>
                    <div class="separator separator-dashed mb-5"></div>
                    <div v-if="license && license.plan" class="d-flex align-items-center justify-content-between bg-light-primary rounded p-5">
                      <div>
                        <div class="fw-bolder fs-4">
                          {{ license.plan.name }}
                          <!-- inscription en attente : offre demandée, activée à la validation -->
                          <a v-if="license.pending_validation" href="#" class="badge badge-warning fs-8 ms-2" @click.prevent="activeTab = 'validation'">{{ $t('landlord.signup.license_pending_badge') }}</a>
                        </div>
                        <div class="text-primary fw-bold">{{ formatPrice(license.plan.price) }}
                          <span v-if="license.plan.billing_period" class="text-muted fs-8"> / {{ $t('landlord.plans.period_' + license.plan.billing_period) }}</span>
                        </div>
                        <div class="text-muted fs-8 mt-1">
                          <span v-if="license.starts_at">{{ $t('landlord.license.from') }} {{ formatDate(license.starts_at) }}</span>
                          <span v-if="license.expires_at"> - {{ $t('landlord.license.until') }} {{ formatDate(license.expires_at) }}</span>
                        </div>
                      </div>
                      <div class="d-flex gap-2">
                        <button @click="showAssignPlan = !showAssignPlan" class="btn btn-sm btn-primary">
                          <i class="fas fa-exchange-alt me-1"></i> {{ $t('landlord.license.change_plan') }}
                        </button>
                        <button v-if="!license.pending_validation" @click="onRevokePlan" class="btn btn-sm btn-danger" :disabled="revokingPlan">
                          <span v-if="revokingPlan" class="spinner-border spinner-border-sm me-1"></span>
                          <i v-else class="fas fa-ban me-1"></i> {{ $t('landlord.license.revoke_plan') }}
                        </button>
                      </div>
                    </div>
                    <div v-else class="d-flex align-items-center justify-content-between bg-light rounded p-5">
                      <span class="text-gray-500">{{ $t('landlord.license.no_plan') }}</span>
                      <button @click="showAssignPlan = !showAssignPlan" class="btn btn-sm btn-primary">
                        <i class="fas fa-plus me-1"></i> {{ $t('landlord.license.assign_plan') }}
                      </button>
                    </div>

                    <!-- Assign plan form -->
                    <div v-if="showAssignPlan" class="border rounded p-4 mt-4">
                      <div class="row g-4 align-items-end">
                        <div class="col-md-4">
                          <label class="form-label fw-bold">{{ $t('landlord.license.select_plan') }}</label>
                          <select v-model="assignForm.plan_id" class="form-select form-select-solid">
                            <option value="">--</option>
                            <option v-for="p in availablePlans" :key="p.id" :value="p.id">{{ p.name }} ({{ formatPrice(p.price) }})</option>
                          </select>
                        </div>
                        <div v-if="!license?.pending_validation" class="col-md-3">
                          <label class="form-label fw-bold">{{ $t('landlord.license.starts_at') }}</label>
                          <input v-model="assignForm.starts_at" type="date" class="form-control form-control-solid" />
                        </div>
                        <div v-if="!license?.pending_validation" class="col-md-3">
                          <label class="form-label fw-bold">{{ $t('landlord.license.expires_at') }}</label>
                          <input v-model="assignForm.expires_at" type="date" class="form-control form-control-solid" />
                        </div>
                        <div class="col-md-2">
                          <button @click="onAssignPlan" class="btn btn-primary w-100" :disabled="!assignForm.plan_id || assigningPlan">
                            <span v-if="assigningPlan" class="spinner-border spinner-border-sm me-1"></span>
                            {{ $t('landlord.license.assign') }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Modules -->
                  <div>
                    <h5 class="fw-bolder text-gray-800 mb-4">{{ $t('landlord.license.modules_title') }}</h5>
                    <div class="separator separator-dashed mb-5"></div>
                    <div v-if="license && license.modules && license.modules.length">
                      <div v-for="mod in license.modules" :key="mod.id" class="mb-5">
                        <div class="d-flex align-items-center justify-content-between bg-light rounded p-4 mb-2">
                          <div class="d-flex align-items-center">
                            <i :class="[mod.icon || 'fas fa-puzzle-piece', 'fs-3 me-3', mod.is_active ? 'text-primary' : 'text-gray-400']"></i>
                            <div>
                              <span class="fw-bolder" :class="mod.is_active ? '' : 'text-gray-400'">{{ mod.name }}</span>
                              <span class="badge ms-2" :class="sourceClass(mod.source)">{{ $t('landlord.license.source_' + mod.source) }}</span>
                            </div>
                          </div>
                          <div class="d-flex align-items-center gap-2">
                            <button v-if="mod.source === 'override_on' || mod.source === 'override_off'"
                                    @click="onRemoveOverride(mod)" class="btn btn-sm btn-light-warning">
                              <i class="fas fa-undo me-1 fs-8"></i> {{ $t('landlord.license.remove_override') }}
                            </button>
                            <button @click="onToggleModule(mod)" class="btn btn-sm"
                                    :class="mod.is_active ? 'btn-light-danger' : 'btn-light-success'">
                              <i :class="mod.is_active ? 'fas fa-ban me-1' : 'fas fa-check me-1'"></i>
                              {{ mod.is_active ? $t('landlord.license.force_off') : $t('landlord.license.force_on') }}
                            </button>
                          </div>
                        </div>
                        <div v-if="mod.children && mod.children.length" class="ms-10">
                          <div v-for="child in mod.children" :key="child.id" class="d-flex align-items-center justify-content-between border rounded p-3 mb-2">
                            <div class="d-flex align-items-center">
                              <i class="fas fa-angle-right me-3" :class="child.is_active ? 'text-gray-500' : 'text-gray-300'"></i>
                              <span :class="child.is_active ? '' : 'text-gray-400'">{{ child.name }}</span>
                              <span class="badge ms-2" :class="sourceClass(child.source)">{{ $t('landlord.license.source_' + child.source) }}</span>
                            </div>
                            <div class="d-flex align-items-center gap-2">
                              <button v-if="child.source === 'override_on' || child.source === 'override_off'"
                                      @click="onRemoveOverride(child)" class="btn btn-sm btn-light-warning">
                                <i class="fas fa-undo me-1 fs-8"></i> {{ $t('landlord.license.remove_override') }}
                              </button>
                              <button @click="onToggleModule(child)" class="btn btn-sm"
                                      :class="child.is_active ? 'btn-light-danger' : 'btn-light-success'">
                                <i :class="child.is_active ? 'fas fa-ban me-1' : 'fas fa-check me-1'"></i>
                                {{ child.is_active ? $t('landlord.license.force_off') : $t('landlord.license.force_on') }}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center py-10 text-gray-500">
                      {{ $t('landlord.license.no_modules') }}
                    </div>
                  </div>
                </template>
              </div>

              <!-- TAB: Historique des plans -->
              <div v-if="activeTab === 'plan_history'">
                <div v-if="loadingLicense" class="text-center py-10">
                  <span class="spinner-border text-primary"></span>
                </div>
                <template v-else>
                  <div v-if="license && license.plan_history && license.plan_history.length">
                    <div v-for="entry in license.plan_history" :key="entry.id"
                         class="d-flex align-items-center justify-content-between rounded p-4 mb-3"
                         :class="entry.is_active ? 'bg-light-success' : 'bg-light'">
                      <div class="d-flex align-items-center gap-4">
                        <i class="fas fa-file-invoice-dollar fs-2"
                           :class="entry.is_active ? 'text-success' : 'text-gray-400'"></i>
                        <div>
                          <div class="fw-bolder" :class="entry.is_active ? '' : 'text-gray-500'">
                            {{ entry.plan ? entry.plan.name : '—' }}
                          </div>
                          <div class="text-muted fs-8 mt-1">
                            <span v-if="entry.plan && entry.plan.price">
                              {{ formatPrice(entry.plan.price) }}
                              <span v-if="entry.plan.billing_period"> / {{ $t('landlord.plans.period_' + entry.plan.billing_period) }}</span>
                              &nbsp;&bull;&nbsp;
                            </span>
                            <span v-if="entry.starts_at">{{ $t('landlord.license.from') }} {{ formatDate(entry.starts_at) }}</span>
                            <span v-if="entry.expires_at"> {{ $t('landlord.license.until') }} {{ formatDate(entry.expires_at) }}</span>
                            <span v-else-if="entry.starts_at"> &bull; {{ $t('landlord.license.history_status_no_expiry') }}</span>
                          </div>
                        </div>
                      </div>
                      <span class="badge fs-8" :class="planHistoryStatusClass(entry)">
                        {{ $t('landlord.license.history_status_' + planHistoryStatus(entry)) }}
                      </span>
                    </div>
                  </div>
                  <div v-else class="text-center py-10 text-gray-500">
                    {{ $t('landlord.license.no_history') }}
                  </div>
                </template>
              </div>

              <!-- TAB: Personnalisation -->
              <div v-if="activeTab === 'branding'">
                <div v-if="loadingBranding" class="text-center py-10">
                  <span class="spinner-border text-primary"></span>
                </div>
                <template v-else>
                  <div class="row g-8 mb-8">
                    <div class="col-12">
                      <label class="form-label fw-bold fs-6">{{ $t('landlord.branding.home_layout_mode') }}</label>
                      <div class="row g-4">
                        <div class="col-md-6">
                          <div class="card card-bordered h-100" :class="{ 'border-primary': brandingForm.home_layout_mode === 'vitrine' }"
                               style="cursor:pointer" @click="brandingForm.home_layout_mode = 'vitrine'">
                            <div class="card-body py-5 px-6">
                              <div class="form-check mb-2">
                                <input class="form-check-input" type="radio" value="vitrine" v-model="brandingForm.home_layout_mode" id="home_mode_vitrine" />
                                <label class="form-check-label fw-bold" for="home_mode_vitrine">
                                  {{ $t('landlord.branding.home_mode_vitrine') }}
                                </label>
                              </div>
                              <div class="text-muted fs-7">
                                {{ $t('landlord.branding.home_mode_vitrine_desc') }}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="col-md-6">
                          <div class="card card-bordered h-100" :class="{ 'border-primary': brandingForm.home_layout_mode === 'catalogue' }"
                               style="cursor:pointer" @click="brandingForm.home_layout_mode = 'catalogue'">
                            <div class="card-body py-5 px-6">
                              <div class="form-check mb-2">
                                <input class="form-check-input" type="radio" value="catalogue" v-model="brandingForm.home_layout_mode" id="home_mode_catalogue" />
                                <label class="form-check-label fw-bold" for="home_mode_catalogue">
                                  {{ $t('landlord.branding.home_mode_catalogue') }}
                                </label>
                              </div>
                              <div class="text-muted fs-7">
                                {{ $t('landlord.branding.home_mode_catalogue_desc') }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="col-12">
                      <label class="form-label fw-bold fs-6">{{ $t('landlord.branding.add_to_cart_label') }}</label>
                      <input type="text" v-model="brandingForm.add_to_cart_label" class="form-control form-control-solid"
                             maxlength="50" :placeholder="$t('landlord.branding.add_to_cart_label_placeholder')" />
                      <div class="text-muted fs-8 mt-1">{{ $t('landlord.branding.add_to_cart_label_hint') }}</div>
                    </div>

                    <div class="col-12">
                      <div class="form-check form-switch form-check-custom form-check-solid">
                        <input class="form-check-input" type="checkbox" v-model="brandingForm.ask_pickup_time" id="branding_ask_pickup_time" />
                        <label class="form-check-label fw-bold fs-6 text-gray-800" for="branding_ask_pickup_time">
                          {{ $t('landlord.branding.ask_pickup_time') }}
                        </label>
                      </div>
                      <div class="text-muted fs-8 mt-1">{{ $t('landlord.branding.ask_pickup_time_hint') }}</div>
                    </div>
                  </div>

                  <div class="separator separator-dashed mb-8"></div>

                  <div class="row g-8 mb-8">
                    <div class="col-md-4">
                      <label class="form-label fw-bold fs-6">{{ $t('landlord.branding.primary_color') }}</label>
                      <div class="d-flex align-items-center gap-3">
                        <input type="color" v-model="brandingForm.primary_color"
                               class="form-control form-control-color rounded" style="width:56px;height:40px;padding:2px 4px;" />
                        <input type="text" v-model="brandingForm.primary_color"
                               class="form-control form-control-solid" maxlength="7"
                               :placeholder="$t('landlord.branding.color_placeholder')" />
                      </div>
                      <div v-if="brandingForm.primary_color" class="mt-2 rounded p-3"
                           :style="{ backgroundColor: brandingForm.primary_color + '1a' }">
                        <span class="fw-bold" :style="{ color: brandingForm.primary_color }">Aa — aperçu couleur principale</span>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label fw-bold fs-6">{{ $t('landlord.branding.sidebar_bg_color') }}</label>
                      <div class="d-flex align-items-center gap-3">
                        <input type="color" v-model="brandingForm.sidebar_bg_color"
                               class="form-control form-control-color rounded" style="width:56px;height:40px;padding:2px 4px;" />
                        <input type="text" v-model="brandingForm.sidebar_bg_color"
                               class="form-control form-control-solid" maxlength="7"
                               :placeholder="$t('landlord.branding.color_placeholder')" />
                      </div>
                      <div v-if="brandingForm.sidebar_bg_color" class="mt-2 rounded p-3 d-flex align-items-center gap-3"
                           :style="{ backgroundColor: brandingForm.sidebar_bg_color }">
                        <div class="rounded" style="width:8px;height:32px;background:rgba(255,255,255,0.3)"></div>
                        <span class="fw-bold" :style="{ color: brandingForm.sidebar_text_color || '#3f4254' }">Menu</span>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label fw-bold fs-6">{{ $t('landlord.branding.sidebar_text_color') }}</label>
                      <div class="d-flex align-items-center gap-3">
                        <input type="color" v-model="brandingForm.sidebar_text_color"
                               class="form-control form-control-color rounded" style="width:56px;height:40px;padding:2px 4px;" />
                        <input type="text" v-model="brandingForm.sidebar_text_color"
                               class="form-control form-control-solid" maxlength="7"
                               :placeholder="$t('landlord.branding.color_placeholder')" />
                      </div>
                    </div>
                  </div>
                  <!-- Image de fond de la page de connexion (boutique en ligne) -->
                  <div class="separator separator-dashed my-6"></div>
                  <h5 class="fw-bolder mb-1">Image de fond de la page de connexion</h5>
                  <div class="text-muted fs-7 mb-4">
                    Affichée derrière le formulaire de connexion / inscription de la boutique en ligne.
                    Sans image, le fond par défaut est utilisé. Format paysage conseillé (1920×1080), 4 Mo max.
                  </div>
                  <div class="d-flex align-items-center gap-5 flex-wrap">
                    <div class="rounded border overflow-hidden d-flex align-items-center justify-content-center bg-light"
                         style="width:240px;height:135px;">
                      <img v-if="loginBackgroundUrl" :src="loginBackgroundUrl" style="width:100%;height:100%;object-fit:cover;" />
                      <span v-else class="text-muted fs-7">Image par défaut</span>
                    </div>
                    <div class="d-flex flex-column gap-2">
                      <input ref="loginBgInput" type="file" accept="image/jpeg,image/png,image/webp" class="d-none" @change="uploadLoginBackground" />
                      <button type="button" class="btn btn-sm btn-light-primary" :disabled="savingLoginBackground" @click="$refs.loginBgInput.click()">
                        <span v-if="savingLoginBackground" class="spinner-border spinner-border-sm me-1"></span>
                        <i v-else class="fas fa-upload me-1"></i>{{ loginBackgroundUrl ? "Changer l'image" : 'Choisir une image' }}
                      </button>
                      <button v-if="loginBackgroundUrl" type="button" class="btn btn-sm btn-light-danger" :disabled="savingLoginBackground" @click="deleteLoginBackground">
                        <i class="fas fa-undo me-1"></i>Revenir à l'image par défaut
                      </button>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end gap-3 mt-4">
                    <button type="button" @click="resetBranding" class="btn btn-light" :disabled="savingBranding">
                      {{ $t('landlord.branding.reset') }}
                    </button>
                    <button type="button" @click="saveBranding" class="btn btn-primary" :disabled="savingBranding">
                      <span v-if="savingBranding" class="spinner-border spinner-border-sm me-1"></span>
                      {{ $t('landlord.branding.save') }}
                    </button>
                  </div>
                </template>
              </div>

            </div>
          </div>
        </template>

      </div>
    </div>
  </div>

  <ShopUserModal ref="userModal" :user="selectedUser" @submit="onUserModalSubmit" />
</template>

<script>
import landlordAxios from '@/services/landlordAxios'
import { mapActions, mapGetters } from 'vuex'
import ShopUserModal from '../components/ShopUserModal.vue'
import InputTel from '@/components/InputTel.vue'

export default {
    name: 'LandlordShopView',
    components: { ShopUserModal, InputTel },
    data() {
        return {
            shop: null,
            loading: true,
            activeTab: 'stats',
            editMode: false,
            saving: false,
            editForm: {},
            editFiles: { logo: null, favicon: null },
            editPreviews: { logo: null, favicon: null },
            defaultImage: '/assets/media/svg/files/blank-image.svg',
            domainInput: '',
            savingDomain: false,
            users: [],
            loadingUsers: false,
            selectedUser: null,
            stats: null,
            topCountries: [],
            lastVisits: [],
            pwaInstalls: null,
            loadingStats: false,
            statsError: null,
            license: null,
            loadingLicense: false,
            showAssignPlan: false,
            assigningPlan: false,
            revokingPlan: false,
            assignForm: { plan_id: '', starts_at: '', expires_at: '' },
            availablePlans: [],
            loadingBranding: false,
            savingBranding: false,
            brandingLoaded: false,
            loginBackgroundUrl: null,
            savingLoginBackground: false,
            brandingForm: { primary_color: '#0b3d87', sidebar_bg_color: '#ffffff', sidebar_text_color: '#3f4254', home_layout_mode: 'vitrine', add_to_cart_label: '', ask_pickup_time: false },
            limitsInput: { max_categories: 0, max_products: 0 },
            savingLimits: false,
            validating: false,
        }
    },
    computed: {
        ...mapGetters('landlord', ['countries', 'sectors']),

        pwaPlatforms() {
            const counts = this.pwaInstalls?.by_platform || {}
            return [
                { key: 'android', label: 'Android', icon: 'fab fa-android text-success', bar: 'bg-success', track: 'bg-light-success', count: counts.android || 0 },
                { key: 'ios', label: 'iPhone / iPad', icon: 'fab fa-apple text-dark', bar: 'bg-dark', track: 'bg-light-dark', count: counts.ios || 0 },
                { key: 'desktop', label: this.$t('landlord.shops.stats_pwa_desktop'), icon: 'fas fa-desktop text-info', bar: 'bg-info', track: 'bg-light-info', count: counts.desktop || 0 },
            ]
        },

        shopId() {
            return this.$route.params.id
        },

        logoEditBackground() {
            const url = this.editPreviews.logo || this.shop?.logo || this.defaultImage
            return { backgroundImage: `url(${url})` }
        },

        faviconEditBackground() {
            const url = this.editPreviews.favicon || this.shop?.favicon || this.defaultImage
            return { backgroundImage: `url(${url})` }
        },
    },
    mounted() {
        this.loadShop()
        this.fetchCountries()
        this.fetchSectors()
    },
    methods: {
        // drapeau à partir du code pays (BJ → 🇧🇯)
        flag(code) {
            if (!code || code.length !== 2) return '🌍'
            return String.fromCodePoint(...code.toUpperCase().split('').map(ch => 127397 + ch.charCodeAt(0)))
        },

        percent(value, total) {
            return total ? Math.round((value / total) * 100) : 0
        },

        visitDate(iso) {
            return new Date(iso).toLocaleString(i18n.global.locale.value || i18n.global.locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        },

        // Visites enregistrées avant le suivi de l'origine : rien de connu
        visitSource(source) {
            if (!source) return '—'
            const known = { direct: this.$t('landlord.shops.stats_visit_direct'), facebook: 'Facebook', whatsapp: 'WhatsApp', instagram: 'Instagram', tiktok: 'TikTok', google: 'Google', x: 'X (Twitter)', linkedin: 'LinkedIn', bing: 'Bing' }
            return known[source] || source
        },

        visitDevice(device) {
            if (device === 'android') return { icon: 'fab fa-android text-success', label: 'Android' }
            if (device === 'ios') return { icon: 'fab fa-apple text-dark', label: 'iPhone / iPad' }
            if (device === 'desktop') return { icon: 'fas fa-desktop text-info', label: this.$t('landlord.shops.stats_pwa_desktop') }
            return { icon: '', label: '—' }
        },

        ...mapActions('landlord', [
            'fetchShop', 'updateShop', 'toggleShopStatus', 'setLimits',
            'fetchDomain', 'updateDomain',
            'fetchShopUsers', 'createShopUser', 'updateShopUser', 'toggleShopUserActive',
            'fetchShopStats', 'fetchShopLicense', 'assignPlan', 'revokePlan', 'toggleModule', 'removeOverride',
            'fetchPlans', 'fetchShopBranding', 'updateShopBranding', 'fetchCountries', 'fetchSectors', 'validateSignup',
        ]),

        onEditFileChange(event, field) {
            const file = event.target.files[0] || null
            this.editFiles[field] = file
            this.editPreviews[field] = file ? URL.createObjectURL(file) : null
        },

        removeEditImageFile(field) {
            this.editFiles[field] = null
            this.editPreviews[field] = null
        },

        async loadShop() {
            this.loading = true
            try {
                const res = await this.fetchShop(this.shopId)
                this.shop = res.data.data
                this.limitsInput = {
                    max_categories: this.shop.max_categories ?? 0,
                    max_products:   this.shop.max_products   ?? 0,
                }
                this.domainInput = this.shop.domain || ''
                // inscription à valider : on ouvre directement l'onglet Validation
                if (this.shop.signup?.pending && this.activeTab === 'stats') this.activeTab = 'validation'
                this.loadStats()
            } finally {
                this.loading = false
            }
        },

        loadStats() {
            this.loadingStats = true
            this.statsError = null
            this.fetchShopStats(this.shopId).then((res) => {
                if (res.data.data.stats.error) {
                    this.statsError = res.data.data.stats.error
                    this.stats = null
                    this.topCountries = []
                    this.lastVisits = []
                } else {
                    this.stats = res.data.data.stats
                    this.topCountries = res.data.data.top_countries || []
                    this.lastVisits = res.data.data.last_visits || []
                    this.pwaInstalls = res.data.data.pwa_installs || null
                }
            }).catch(() => {
                this.statsError = i18n.global.t('landlord.shops.stats_error')
                this.stats = null
                this.topCountries = []
                this.lastVisits = []
            }).finally(() => {
                this.loadingStats = false
            })
        },

        switchToInfo() {
            this.activeTab = 'info'
            this.editMode = false
        },

        // démarre l'abonnement (ou les 14 jours d'essai) et envoie ses accès au client
        onValidateSignup() {
            Swal.fire({
                text: this.$t('landlord.shops.confirm_validate', { name: this.shop.name || this.shop.id }),
                icon: 'question',
                showCancelButton: true,
                buttonsStyling: false,
                confirmButtonText: i18n.global.t('swal.yes'),
                cancelButtonText: i18n.global.t('swal.no'),
                customClass: { confirmButton: 'btn fw-bold btn-success', cancelButton: 'btn fw-bold btn-secondary' },
            }).then((result) => {
                if (!result.value) return
                this.validating = true
                this.validateSignup({ shopId: this.shopId }).then((response) => {
                    window.toastr['success'](response.data.message)
                    this.license = null
                    this.loadShop()
                }).catch((error) => {
                    window.toastr['error'](error.response?.data?.message || i18n.global.t('landlord.shops.validate_failed'))
                }).finally(() => { this.validating = false })
            })
        },

        switchToDomain() {
            this.activeTab = 'domain'
        },

        switchToUsers() {
            this.activeTab = 'users'
            if (!this.users.length) this.loadUsers()
        },

        loadUsers() {
            this.loadingUsers = true
            this.fetchShopUsers(this.shopId).then((res) => {
                this.users = res.data.data
            }).finally(() => {
                this.loadingUsers = false
            })
        },

        defaultUserForm() {
            return { lastname: '', firstname: '', email: '', phone: '', password: '', role: 'Owner' }
        },

        onCreateUser() {
            this.creatingUser = true
            this.userErrors = {}
            this.createShopUser({ shopId: this.shopId, data: this.userForm }).then((res) => {
                this.users.push(res.data.data)
                this.showUserForm = false
                this.userForm = this.defaultUserForm()
                window.toastr['success'](i18n.global.t('landlord.shops.user_created'))
            }).catch((err) => {
                if (err.response?.status === 422) {
                    this.userErrors = err.response.data.errors || {}
                }
            }).finally(() => {
                this.creatingUser = false
            })
        },

        onToggleUserActive(user) {
            this.toggleShopUserActive({ shopId: this.shopId, userId: user.id }).then((res) => {
                user.is_active = res.data.is_active
            })
        },

        openUserModal(user) {
            this.selectedUser = user
            this.$nextTick(() => this.$refs.userModal.open())
        },

        onUserModalSubmit({ data, onSuccess, onError }) {
            if (this.selectedUser) {
                this.updateShopUser({ shopId: this.shopId, userId: this.selectedUser.id, data })
                    .then((res) => {
                        const idx = this.users.findIndex(u => u.id === this.selectedUser.id)
                        if (idx !== -1) this.users.splice(idx, 1, { ...this.users[idx], ...res.data.data })
                        window.toastr['success'](i18n.global.t('landlord.shops.user_updated'))
                        onSuccess()
                    }).catch((err) => {
                        onError(err.response?.data?.errors || {})
                    })
            } else {
                this.createShopUser({ shopId: this.shopId, data })
                    .then((res) => {
                        this.users.push(res.data.data)
                        window.toastr['success'](i18n.global.t('landlord.shops.user_created'))
                        onSuccess()
                    }).catch((err) => {
                        onError(err.response?.data?.errors || {})
                    })
            }
        },

        cancelEdit() {
            this.editMode = false
            this.editForm = {}
            this.editFiles = { logo: null, favicon: null }
            this.editPreviews = { logo: null, favicon: null }
        },

        onSave() {
            this.saving = true
            const hasFiles = this.editFiles.logo || this.editFiles.favicon
            let data

            if (hasFiles) {
                data = new FormData()
                const keys = ['name', 'address', 'phone', 'phone_code', 'country_id', 'sector_id', 'society_email', 'society_phone', 'society_phone_code']
                keys.forEach(key => {
                    if (this.editForm[key] !== undefined && this.editForm[key] !== '') {
                        data.append(key, this.editForm[key])
                    }
                })
                if (this.editFiles.logo) data.append('logo', this.editFiles.logo)
                if (this.editFiles.favicon) data.append('favicon', this.editFiles.favicon)
            } else {
                data = { ...this.editForm }
            }

            this.updateShop({ shopId: this.shopId, data }).then((res) => {
                this.shop = res.data.data
                this.editMode = false
                this.editForm = {}
                this.editFiles = { logo: null, favicon: null }
                this.editPreviews = { logo: null, favicon: null }
                window.toastr['success'](i18n.global.t('landlord.shops.updated'))
            }).finally(() => {
                this.saving = false
            })
        },

        onSetLimits() {
            this.savingLimits = true
            this.setLimits({ shopId: this.shopId, limits: this.limitsInput }).then((res) => {
                this.shop = res.data.data
                window.toastr['success'](i18n.global.t('landlord.shops.limits_updated'))
            }).finally(() => {
                this.savingLimits = false
            })
        },

        onSaveDomain() {
            this.savingDomain = true
            this.updateDomain({ shopId: this.shopId, domain: this.domainInput }).then((res) => {
                this.shop.domain = res.data.domain
                window.toastr['success'](i18n.global.t('landlord.shops.domain_updated'))
            }).finally(() => {
                this.savingDomain = false
            })
        },

        onToggleStatus() {
            const action = this.shop.online ? this.$t('landlord.shops.deactivate') : this.$t('landlord.shops.activate')
            Swal.fire({
                text: `${action} "${this.shop.name || this.shopId}" ?`,
                icon: 'warning',
                showCancelButton: true,
                buttonsStyling: false,
                confirmButtonText: i18n.global.t('swal.yes'),
                cancelButtonText: i18n.global.t('swal.no'),
                customClass: {
                    confirmButton: 'btn fw-bold btn-primary',
                    cancelButton: 'btn fw-bold btn-secondary'
                }
            }).then((result) => {
                if (result.value) {
                    this.toggleShopStatus(this.shopId).then((res) => {
                        this.shop = res.data.data
                        window.toastr['success'](i18n.global.t('landlord.shops.status_updated'))
                    })
                }
            })
        },

        // License
        switchToLicense() {
            this.activeTab = 'license'
            if (!this.license) this.loadLicense()
        },

        async loadLicense() {
            this.loadingLicense = true
            try {
                const [licenseRes, plansRes] = await Promise.all([
                    this.fetchShopLicense(this.shopId),
                    this.fetchPlans(),
                ])
                this.license = licenseRes.data.data
                this.availablePlans = plansRes.data.data
            } finally {
                this.loadingLicense = false
            }
        },

        onAssignPlan() {
            this.assigningPlan = true
            this.assignPlan({ shopId: this.shopId, data: this.assignForm }).then((res) => {
                this.license = res.data.data
                this.showAssignPlan = false
                this.assignForm = { plan_id: '', starts_at: '', expires_at: '' }
                window.toastr['success'](res.data.message || i18n.global.t('landlord.license.plan_assigned'))
                // demande en attente : l'offre affichée dans l'en-tête et l'onglet Validation change aussi
                if (this.shop.signup?.pending) this.fetchShop(this.shopId).then((r) => { this.shop = r.data.data })
            }).finally(() => {
                this.assigningPlan = false
            })
        },

        onRevokePlan() {
            Swal.fire({
                text: this.$t('landlord.license.revoke_confirm'),
                icon: 'warning',
                showCancelButton: true,
                buttonsStyling: false,
                confirmButtonText: i18n.global.t('landlord.license.revoke_plan'),
                cancelButtonText: i18n.global.t('landlord.shops.cancel'),
                customClass: {
                    confirmButton: 'btn fw-bold btn-danger',
                    cancelButton: 'btn fw-bold btn-secondary'
                }
            }).then((result) => {
                if (!result.value) return
                this.revokingPlan = true
                this.revokePlan(this.shopId).then((res) => {
                    this.license = res.data.data
                    window.toastr['success'](i18n.global.t('landlord.license.plan_revoked'))
                }).finally(() => {
                    this.revokingPlan = false
                })
            })
        },

        onToggleModule(mod) {
            this.toggleModule({
                shopId: this.shopId,
                data: { module_id: mod.id, is_active: !mod.is_active }
            }).then((res) => {
                this.license = res.data.data
                window.toastr['success'](i18n.global.t('landlord.license.module_updated'))
            })
        },

        onRemoveOverride(mod) {
            this.removeOverride({ shopId: this.shopId, moduleId: mod.id }).then((res) => {
                this.license = res.data.data
                window.toastr['success'](i18n.global.t('landlord.license.override_removed'))
            })
        },

        switchToPlanHistory() {
            this.activeTab = 'plan_history'
            if (!this.license) this.loadLicense()
        },

        // Branding
        switchToBranding() {
            this.activeTab = 'branding'
            if (!this.brandingLoaded) this.loadBranding()
        },

        async loadBranding() {
            this.loadingBranding = true
            try {
                const res = await this.fetchShopBranding(this.shopId)
                const b = res.data.branding
                this.brandingForm = {
                    primary_color: b.primary_color || '#0b3d87',
                    sidebar_bg_color: b.sidebar_bg_color || '#ffffff',
                    sidebar_text_color: b.sidebar_text_color || '#3f4254',
                    home_layout_mode: b.home_layout_mode || 'vitrine',
                    add_to_cart_label: b.add_to_cart_label || '',
                    ask_pickup_time: !!b.ask_pickup_time,
                }
                this.loginBackgroundUrl = b.login_background_url || null
                this.brandingLoaded = true
            } finally {
                this.loadingBranding = false
            }
        },

        saveBranding() {
            this.savingBranding = true
            this.updateShopBranding({ shopId: this.shopId, data: this.brandingForm }).then(() => {
                window.toastr['success'](i18n.global.t('landlord.branding.saved'))
            }).finally(() => {
                this.savingBranding = false
            })
        },

        async uploadLoginBackground(event) {
            const file = event.target.files?.[0]
            event.target.value = ''
            if (!file) return
            const data = new FormData()
            data.append('image', file)
            this.savingLoginBackground = true
            try {
                const res = await landlordAxios.post(`landlord/shops/${this.shopId}/branding/login-background`, data)
                this.loginBackgroundUrl = res.data.login_background_url
                window.toastr['success'](res.data.message)
            } catch (e) {
                // message d'erreur déjà affiché par landlordAxios
            } finally {
                this.savingLoginBackground = false
            }
        },

        async deleteLoginBackground() {
            this.savingLoginBackground = true
            try {
                const res = await landlordAxios.delete(`landlord/shops/${this.shopId}/branding/login-background`)
                this.loginBackgroundUrl = null
                window.toastr['success'](res.data.message)
            } finally {
                this.savingLoginBackground = false
            }
        },

        resetBranding() {
            this.brandingForm = {
                primary_color: '#0b3d87',
                sidebar_bg_color: '#ffffff',
                sidebar_text_color: '#3f4254',
                home_layout_mode: this.brandingForm.home_layout_mode,
                add_to_cart_label: this.brandingForm.add_to_cart_label,
                ask_pickup_time: this.brandingForm.ask_pickup_time,
            }
            this.saveBranding()
        },

        formatDate(date) {
            if (!date) return ''
            return new Date(date).toLocaleDateString('fr-FR')
        },

        formatPrice(price) {
            return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA'
        },

        sourceClass(source) {
            const map = {
                plan: 'badge-light-primary',
                override_on: 'badge-light-success',
                override_off: 'badge-light-danger',
                disabled: 'badge-light',
            }
            return map[source] || 'badge-light'
        },

        planHistoryStatus(entry) {
            const now = new Date()
            if (entry.is_active) return 'active'
            if (entry.expires_at && new Date(entry.expires_at) < now) return 'expired'
            if (entry.starts_at && new Date(entry.starts_at) > now) return 'future'
            return 'expired'
        },

        planHistoryStatusClass(entry) {
            const status = this.planHistoryStatus(entry)
            const map = { active: 'badge-success', expired: 'badge-secondary', future: 'badge-light-warning' }
            return map[status] || 'badge-secondary'
        },
    },
    watch: {
        editMode(val) {
            if (val && this.shop) {
                this.editForm = {
                    name: this.shop.name || '',
                    address: this.shop.address || '',
                    phone: this.shop.phone || '',
                    phone_code: this.shop.phone_code || '',
                    country_id: this.shop.country_id || '',
                    sector_id: this.shop.sector_id || '',
                    society_email: this.shop.society_email || '',
                    society_phone: this.shop.society_phone || '',
                    society_phone_code: this.shop.society_phone_code || '',
                }
                this.$nextTick(() => {
                    if (typeof KTImageInput !== 'undefined') KTImageInput.init()
                })
            }
        }
    }
}
</script>
